import { TokenProvider } from '../ports'
import {
  DecodeError,
  decodeArray,
  decodeChat,
  decodeContact,
  decodeMessage,
  decodeStringArray,
  decodeUnreadCount,
} from '../protocol/decode'
import {
  Attachment,
  Chat,
  Contact,
  SentMessage,
  UnreadCount,
} from '../protocol/types'
import { ChatError, Result, err, fold, mapOk, ok } from '../result'

const REQUEST_TIMEOUT_MS = 15000

export interface ChatApiConfig {
  readonly host: string
  readonly hostHeader?: string
  readonly getToken: TokenProvider
  readonly onDecodeErrors?: (route: string, errors: DecodeError[]) => void
}

export interface MessagePage {
  readonly limit: number
  readonly before?: number
}

export interface ChatApi {
  getInbox(userId: string): Promise<Result<Chat[], ChatError>>
  getArchivedChats(userId: string): Promise<Result<Chat[], ChatError>>
  getMessagesByChannel(
    channelId: string,
    page?: MessagePage,
  ): Promise<Result<SentMessage[], ChatError>>
  getUnreadCounts(userId: string): Promise<Result<UnreadCount[], ChatError>>
  getMessagesByIds(
    messageIds: string[],
  ): Promise<Result<SentMessage[], ChatError>>
  getContactsByIds(contactIds: string[]): Promise<Result<Contact[], ChatError>>
  getOnlineContacts(): Promise<Result<string[], ChatError>>
  getDirectChannelId(
    userId: string,
    recipientId: string,
  ): Promise<Result<string | null, ChatError>>
  createChat(
    name: string,
    isGroup: boolean,
    creatorId: string,
    memberIds: string[],
    avatarUrl: string,
  ): Promise<Result<string, ChatError>>
  updateChat(chat: Chat): Promise<Result<void, ChatError>>
  sendMessage(
    channelId: string,
    text: string,
    authorId: string,
    fakeId: string,
    attachments: readonly Attachment[],
  ): Promise<Result<string, ChatError>>
  setLastReadMessage(
    channelId: string,
    messageId: string,
    userId: string,
  ): Promise<Result<void, ChatError>>
  archiveChat(
    channelId: string,
    userId: string,
  ): Promise<Result<void, ChatError>>
  unarchiveChat(
    channelId: string,
    userId: string,
  ): Promise<Result<void, ChatError>>
  leaveChat(channelId: string, userId: string): Promise<Result<void, ChatError>>
}

export class HttpChatApi implements ChatApi {
  public constructor(private readonly config: ChatApiConfig) {}

  public getInbox(userId: string): Promise<Result<Chat[], ChatError>> {
    return this.getDecodedArray(`chats/${userId}`, decodeChat)
  }

  public getArchivedChats(userId: string): Promise<Result<Chat[], ChatError>> {
    return this.getDecodedArray(`chats/${userId}/archived`, decodeChat)
  }

  // No page → the bare legacy route: the old server has no pagination and the
  // new one keeps the no-param path byte-identical.
  public getMessagesByChannel(
    channelId: string,
    page?: MessagePage,
  ): Promise<Result<SentMessage[], ChatError>> {
    const query = page
      ? `?limit=${page.limit}` +
        (page.before !== undefined ? `&before=${page.before}` : '')
      : ''
    return this.getDecodedArray(
      `messages/channel/${channelId}${query}`,
      decodeMessage,
    )
  }

  public getUnreadCounts(
    userId: string,
  ): Promise<Result<UnreadCount[], ChatError>> {
    return this.getDecodedArray(
      `messages/unread-counts/${userId}`,
      decodeUnreadCount,
    )
  }

  public async getMessagesByIds(
    messageIds: string[],
  ): Promise<Result<SentMessage[], ChatError>> {
    if (!messageIds.length) {
      return ok([])
    }

    const raw = await this.request('messages', 'POST', { messageIds })
    return mapOk(raw, value => this.collect('messages', value, decodeMessage))
  }

  public async getContactsByIds(
    contactIds: string[],
  ): Promise<Result<Contact[], ChatError>> {
    if (!contactIds.length) {
      return ok([])
    }

    const raw = await this.request('contacts', 'POST', { contactIds })
    return mapOk(raw, value => this.collect('contacts', value, decodeContact))
  }

  public async getOnlineContacts(): Promise<Result<string[], ChatError>> {
    const raw = await this.request('online-contacts', 'GET')
    return mapOk(raw, decodeStringArray)
  }

  // The direct-channel lookup answers "is there a conversation with this
  // person" — a missing one is a normal answer, so the 404 is decoded into
  // the value channel instead of surfacing as an error.
  public async getDirectChannelId(
    userId: string,
    recipientId: string,
  ): Promise<Result<string | null, ChatError>> {
    const raw = await this.request(`chats/${userId}/${recipientId}`, 'GET')
    return fold(
      raw,
      value => this.expectId('direct channel lookup', value),
      error =>
        error.kind === 'http' && error.status === 404 ? ok(null) : err(error),
    )
  }

  public async createChat(
    name: string,
    isGroup: boolean,
    creatorId: string,
    memberIds: string[],
    avatarUrl: string,
  ): Promise<Result<string, ChatError>> {
    const raw = await this.request('chats', 'POST', {
      name,
      isGroup,
      creatorId,
      memberIds,
      avatarUrl,
    })
    return fold(
      raw,
      value => this.expectId('create chat', value),
      error => err(error),
    )
  }

  // The update contract is a whole-object replace: the server diffs the full
  // member list itself, and the avatar wire name differs from create
  // (`avatar` here, `avatarUrl` there).
  public async updateChat(chat: Chat): Promise<Result<void, ChatError>> {
    const raw = await this.request('chats', 'PUT', {
      chat: {
        id: chat.id,
        name: chat.name,
        avatar: chat.avatar || '',
        memberIds: chat.memberIds,
      },
    })
    return mapOk(raw, () => undefined)
  }

  public async sendMessage(
    channelId: string,
    text: string,
    authorId: string,
    fakeId: string,
    attachments: readonly Attachment[],
  ): Promise<Result<string, ChatError>> {
    const raw = await this.request(`messages/channel/${channelId}`, 'POST', {
      text,
      authorId,
      fakeId,
      attachments,
    })
    return fold(
      raw,
      value => this.expectId('send message', value),
      error => err(error),
    )
  }

  private expectId(operation: string, raw: unknown): Result<string, ChatError> {
    return typeof raw === 'string' && raw
      ? ok(raw)
      : err({
          kind: 'decode',
          detail: `${operation} response is not an id: ${JSON.stringify(raw)}`,
        })
  }

  public async setLastReadMessage(
    channelId: string,
    messageId: string,
    userId: string,
  ): Promise<Result<void, ChatError>> {
    const raw = await this.request('chats/set-last-read-message', 'POST', {
      channelId,
      messageId,
      userId,
    })
    return mapOk(raw, () => undefined)
  }

  public async archiveChat(
    channelId: string,
    userId: string,
  ): Promise<Result<void, ChatError>> {
    const raw = await this.request(`chats/${channelId}/archive`, 'POST', {
      userId,
    })
    return mapOk(raw, () => undefined)
  }

  public async unarchiveChat(
    channelId: string,
    userId: string,
  ): Promise<Result<void, ChatError>> {
    const raw = await this.request(`chats/${channelId}/unarchive`, 'POST', {
      userId,
    })
    return mapOk(raw, () => undefined)
  }

  public async leaveChat(
    channelId: string,
    userId: string,
  ): Promise<Result<void, ChatError>> {
    const raw = await this.request(`chats/${channelId}/leave`, 'POST', {
      userId,
    })
    return mapOk(raw, () => undefined)
  }

  private async getDecodedArray<T>(
    route: string,
    decoder: (raw: unknown) => Result<T, DecodeError>,
  ): Promise<Result<T[], ChatError>> {
    const raw = await this.request(route, 'GET')
    return mapOk(raw, value => this.collect(route, value, decoder))
  }

  private collect<T>(
    route: string,
    raw: unknown,
    decoder: (item: unknown) => Result<T, DecodeError>,
  ): T[] {
    const { values, errors } = decodeArray(raw, decoder)
    if (errors.length) {
      this.config.onDecodeErrors?.(route, errors)
    }
    return values
  }

  private async request(
    route: string,
    method: 'GET' | 'POST' | 'PUT',
    body?: unknown,
  ): Promise<Result<unknown, ChatError>> {
    const { host, hostHeader, getToken } = this.config
    const protocol = host.startsWith('http') ? '' : 'https://'
    const url = `${protocol}${host}/chat-service/${route}`

    let token: string
    try {
      token = await getToken()
    } catch (e) {
      return err({ kind: 'network', message: `token: ${String(e)}` })
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(url, {
        method,
        signal: controller.signal,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(hostHeader ? { host: hostHeader } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      })

      if (!response.ok) {
        return err({ kind: 'http', status: response.status })
      }

      // Some routes respond 200 with an empty or plain-text body
      // (e.g. sendStatus(200) -> "OK"); only JSON bodies are parsed.
      const text = await response.text()
      if (!text) {
        return ok(null)
      }
      try {
        return ok(JSON.parse(text))
      } catch {
        return ok(text)
      }
    } catch (e) {
      return err({ kind: 'network', message: String(e) })
    } finally {
      clearTimeout(timeout)
    }
  }
}
